import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import Markdown from "markdown-to-jsx"
import SanityImage from "gatsby-plugin-sanity-image"
import CTAButton from "components/buttons/CTAButton"
import theme from "styles/theme"
import useBreakpoints from "hooks/useBreakpoints"
import Container from "components/Container"
import BlockHeader from "components/BlockHeader"
import ScrollIndicator from "components/ScrollIndicator"

const IllustratedFeaturesBlock = ({
  context,
  label,
  title,
  featureBlocks,
  scrollIndicator,
}) => (
  <Container
    wrapperStyles={theme.expandQueries({
      position: "relative",
      padding: ["75px 10px", "65px 10px", "48px 10px"],
      "&:after": {
        content: "''",
        position: "absolute",
        background: theme.white,
        top: [485, 407, 400],
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        "@media (max-width: 504px)": {
          top: "calc(46vw + 175px)",
        },
      },
    })}
    css={{
      position: "relative",
      zIndex: 2,
    }}
  >
    <BlockHeader label={label} title={title} />

    <div css={theme.expandQueries({ marginTop: [70, 60, 40] })}>
      {featureBlocks.map((block, index) => (
        <FeatureBlock key={index} index={index} {...block} />
      ))}
    </div>

    {scrollIndicator && (
      <ScrollIndicator
        fragment={context.nextSectionId}
        css={{
          marginTop: 23,
          transform: "translateY(45px)",
          [theme.tablet]: { marginTop: 0 },
          [theme.mobile]: { transform: "translateY(35px)" },
        }}
      >
        {scrollIndicator}
      </ScrollIndicator>
    )}
  </Container>
)

const FeatureBlock = ({ label, text, action, image, index }) => {
  const { tablet, mobile } = useBreakpoints()

  return (
    <div
      css={[
        theme.expandQueries({
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          minHeight: [410, 355, 320],
          margin: ["20px auto", "12px auto"],
          maxWidth: [943, 800, 500],
          border: `2px solid ${theme.midBlue}`,
        }),

        {
          [theme.mobile]: {
            display: "block",
          },
        },

        index % 2 === 0
          ? {
              backgroundColor: theme.midBlue,
              "--shadowColor": theme.deepBlue,
              ".imageBlock": {
                order: 2,
              },
            }
          : {
              backgroundColor: theme.deepBlue,
              borderColor: theme.deepBlue,
              "--shadowColor": "#016498",
            },
      ]}
    >
      <div
        css={{
          width: "100%",
          height: "100%",
          position: "relative",
          [theme.mobile]: {
            height: "auto",
            position: "unset",
          },
        }}
        className="imageBlock"
      >
        <SanityImage
          {...image}
          width={mobile ? 354 : tablet ? 374 : 472}
          height={mobile ? 320 : tablet ? 355 : 410}
          css={{
            display: "block",
            position: "absolute",
            height: "100%",
            width: "100%",
            objectFit: "cover",
            [theme.mobile]: {
              position: "unset",
              height: "auto",
            },
          }}
        />
      </div>

      <div
        css={theme.expandQueries({
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "start",
          padding: [44, 36, 30],
          color: theme.white,
        })}
      >
        <div>
          <div
            css={{
              ...theme.t5lg,
              color: theme.lightBlue30,
            }}
          >
            {label}
          </div>

          <Markdown
            options={{
              forceBlock: true,
              overrides: {
                // eslint-disable-next-line react/display-name
                strong: props => (
                  <strong
                    css={{
                      background: `linear-gradient(transparent 25%, var(--shadowColor) 25%)`,
                      boxShadow: `0 0.025em 0 var(--shadowColor)`,
                    }}
                    {...props}
                  />
                ),
              },
            }}
            css={{
              ...theme.t4,
              fontWeight: 400,
              margin: "18px 0px 40px",
              a: { textDecoration: "underline" },
              [theme.tablet]: { margin: "20px 0 32px" },
              [theme.mobile]: { margin: "18px 0 30px" },
            }}
          >
            {text}
          </Markdown>
        </div>

        {action && <FeatureCTAButton {...action} />}
      </div>
    </div>
  )
}

const FeatureCTAButton = ({ href, text, ...props }) => (
  <CTAButton
    colorScheme="standard"
    size="md"
    href={href}
    css={{
      "--shadowColor": "inherit",
    }}
    {...props}
  >
    {text}
  </CTAButton>
)

FeatureCTAButton.propTypes = {
  href: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
}

FeatureBlock.propTypes = {
  label: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  action: PropTypes.shape(FeatureCTAButton.propTypes),
  image: PropTypes.shape({
    hotspot: PropTypes.object,
    crop: PropTypes.object,
    asset: PropTypes.object.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
}

IllustratedFeaturesBlock.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  featureBlocks: PropTypes.arrayOf(
    PropTypes.shape({
      ...FeatureBlock.propTypes,
      index: undefined,
    }).isRequired
  ).isRequired,
  scrollIndicator: PropTypes.string,
}

export default IllustratedFeaturesBlock

export const query = graphql`
  fragment IllustratedFeaturesBlock on SanityIllustratedFeaturesBlock {
    label
    title
    featureBlocks {
      label
      text
      image {
        ...Image
      }
      action {
        ...CTAButton
      }
    }
    scrollIndicator
  }
`
